import useResize from '@anm/hooks/useResize';
import noop from 'lodash/fp/noop';
import {
  useEffect,
  useRef,
  useState,
  MutableRefObject,
  RefObject
} from 'react';

const useContainerWidthWatcher = (
  containerRef: RefObject<HTMLDivElement>,
  onWidthChange = noop
) => {
  const [prevContainerWidth, setPrevContainerWidth] = useState<number>();
  useEffect(() => {
    if (!prevContainerWidth) {
      setPrevContainerWidth(containerRef.current?.offsetWidth);
    }

    if (prevContainerWidth !== containerRef.current?.offsetWidth) {
      onWidthChange();
      setPrevContainerWidth(containerRef.current?.offsetWidth);
    }
  }, [containerRef.current?.offsetWidth]);
};

const sliceList = <T>(list: T[]) => (start: number, deleteCount?: number) =>
  [...list]
    .reverse()
    .slice(start, deleteCount)
    .reverse();

type UseResetProps<T> = {
  data: T[];
  isDoneRef: MutableRefObject<boolean>;
  containerRef: RefObject<HTMLDivElement>;
  currentItemIndexRef: MutableRefObject<number>;
  resetDropdownList: () => void;
  resetBreadcrumbList: () => void;
};
const useReset = <T>({
  data,
  isDoneRef,
  containerRef,
  currentItemIndexRef,
  resetDropdownList,
  resetBreadcrumbList
}: UseResetProps<T>) => {
  const resetBreadcrumb = () => {
    isDoneRef.current = false;
    currentItemIndexRef.current = 1;
    resetBreadcrumbList();
  };
  const reset = () => {
    resetBreadcrumb();
    resetDropdownList();
  };

  useContainerWidthWatcher(containerRef, resetBreadcrumb);
  useResize(reset);

  useEffect(() => {
    reset();
  }, [data.length]);
};

export const useBreadcrumbList = <T>(data: T[]) => {
  const getData = sliceList(data);
  const isDoneRef = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerMaxWidth, setContainerMaxWidth] = useState<null | number>(
    null
  );
  const breadcrumbsRef = useRef<HTMLDivElement>(null);
  const currentItemIndexRef = useRef(1);

  const [breadcrumbList, setBreadcrumbList] = useState<T[]>([]);
  const [dropdownList, setDropdownList] = useState<null | T[]>(null);

  useReset({
    data,
    isDoneRef,
    containerRef,
    currentItemIndexRef,
    resetDropdownList: () => setDropdownList(null),
    resetBreadcrumbList: () => {
      setContainerMaxWidth(null);
      setBreadcrumbList([]);
    }
  });

  useEffect(() => {
    if (
      !containerRef.current ||
      !breadcrumbsRef.current ||
      isDoneRef.current ||
      currentItemIndexRef.current > data.length
    ) {
      return;
    }

    if (!containerMaxWidth) {
      setContainerMaxWidth(containerRef.current.offsetWidth);
      return;
    }

    if (containerMaxWidth !== breadcrumbsRef.current.offsetWidth) {
      currentItemIndexRef.current = currentItemIndexRef.current + 1;
      setBreadcrumbList(getData(0, currentItemIndexRef.current));
    } else {
      isDoneRef.current = true;
      currentItemIndexRef.current = currentItemIndexRef.current - 1;
      setDropdownList(getData(currentItemIndexRef.current));
      setBreadcrumbList(getData(0, currentItemIndexRef.current));
    }
  });

  return [
    dropdownList,
    breadcrumbList,
    containerRef,
    breadcrumbsRef,
    containerMaxWidth
  ] as const;
};
